import { getAccessToken } from '../supabaseClient';

interface UploadResult {
  publicUrl: string;
  path: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:4000';

const buildUrl = (path: string) => {
  const base = API_BASE_URL.replace(/\/*$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

const parseError = async (response: Response): Promise<Error> => {
  try {
    const payload = await response.json();
    if (payload?.error) {
      const details = Array.isArray(payload.details)
        ? payload.details.join(', ')
        : typeof payload.details === 'string'
        ? payload.details
        : undefined;
      return new Error(details ? `${payload.error}: ${details}` : payload.error);
    }
  } catch (error) {
    // Ignore JSON parse errors
  }
  return new Error(`Request failed with status ${response.status}`);
};

const resolveFileName = (file: File | Blob, explicitName?: string): string => {
  if (explicitName) {
    return explicitName;
  }

  if (file instanceof File && file.name) {
    return file.name;
  }

  return 'upload';
};

const ensureFileInstance = (input: File | Blob, name: string): File => {
  if (input instanceof File) {
    return input;
  }

  return new File([input], name, { type: input.type || 'application/octet-stream' });
};

const normalizePathInput = (value: string): string | null => {
  if (!value) {
    return null;
  }

  return getStoragePathFromPublicUrl(value) ?? value;
};

export const uploadFileToStorage = async (
  file: File | Blob,
  options: { folder: string; fileName?: string }
): Promise<UploadResult> => {
  const token = await getAccessToken();
  const name = resolveFileName(file, options.fileName);
  const fileForUpload = ensureFileInstance(file, name);

  const formData = new FormData();
  formData.append('file', fileForUpload);
  formData.append('folder', options.folder);
  formData.append('fileName', name);

  const response = await fetch(buildUrl('/api/storage/upload'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: formData
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  const { asset } = (await response.json()) as { asset: UploadResult };
  return asset;
};

export const uploadDataUrlToStorage = async (
  dataUrl: string,
  options: { folder: string; fileName?: string }
): Promise<UploadResult> => {
  const response = await fetch(dataUrl);
  if (!response.ok) {
    throw new Error('Failed to decode image data.');
  }

  const blob = await response.blob();
  const fileName = options.fileName ?? 'image';
  return uploadFileToStorage(blob, { folder: options.folder, fileName });
};

const STORAGE_PATH_REGEX = /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/([^?]+)/i;

export const getStoragePathFromPublicUrl = (publicUrl: string): string | null => {
  if (!publicUrl) {
    return null;
  }

  const match = STORAGE_PATH_REGEX.exec(publicUrl);
  if (!match) {
    return null;
  }

  const encodedPath = match[2];
  try {
    return decodeURIComponent(encodedPath);
  } catch (error) {
    console.warn('Failed to decode storage path from URL', { publicUrl, error });
    return encodedPath;
  }
};

export const deleteAssetByPublicUrl = async (identifier: string) => {
  const path = normalizePathInput(identifier);
  if (!path) {
    return;
  }

  const token = await getAccessToken();
  const response = await fetch(buildUrl('/api/storage/delete'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ path })
  });

  if (!response.ok && response.status !== 404) {
    throw await parseError(response);
  }
};

export const refreshPublicUrl = async (value: string): Promise<string> => {
  const path = normalizePathInput(value);
  if (!path) {
    throw new Error('A valid storage path is required to refresh the asset URL.');
  }

  const token = await getAccessToken();
  const response = await fetch(buildUrl('/api/storage/refresh'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ path })
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  const { publicUrl } = (await response.json()) as { publicUrl: string };
  return publicUrl;
};
